import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { setDialogOpen } from '../../store/Actions';
import { buildDetailPath, useAppNavigate } from '../../utils/Routing';
import { MyAvatar } from '../common/MyAvatar';
import { buildTripSummaries, formatDateRange } from '../../utils/EntitySummaries';
import '../entityView/EntityView.scss';

const sortTripsByMostRecent = (trips, tripSummaries) => {
  return Object.values(trips || {}).sort((a, b) => {
    const aEndAt = tripSummaries[a.id]?.endAt;
    const bEndAt = tripSummaries[b.id]?.endAt;
    if(aEndAt && bEndAt) return bEndAt - aEndAt;
    if(aEndAt) return -1;
    if(bEndAt) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });
};

const TripCard = ({trip, summary, friends, viewingUserAccount, onOpen, delay}) => {
  const visibleFriendIds = (summary?.friendIds || [])
    .filter(id => friends[id])
    .slice(0, 4);
  const totalKnownFriendCount = (summary?.friendIds || [])
    .filter(id => friends[id]).length;
  const hiddenFriendCount = Math.max(totalKnownFriendCount - visibleFriendIds.length, 0);
  const showPeople = summary?.eventCount > 0;
  const dateRange = summary
    ? formatDateRange(summary.startAt, summary.endAt)
    : '';
  const eventCountLabel = summary
    ? `${summary.eventCount} ${summary.eventCount === 1 ? 'event' : 'events'}`
    : 'No events yet';

  return (
    <button
      type="button"
      className="entity-view__card entity-view__card--trip"
      style={{'--card-delay': `${delay}ms`, '--card-accent': '#0ea5e9'}}
      onClick={onOpen}
    >
      {trip.coverPicUrl ? (
        <div
          className="entity-view__trip-cover"
          aria-label={`${trip.name} cover photo`}
          style={{backgroundImage: `url(${trip.coverPicUrl})`}}
        />
      ) : (
        <div
          className="entity-view__trip-cover entity-view__trip-cover--placeholder"
          aria-hidden="true"
        >
          🧳
        </div>
      )}

      <div className="entity-view__trip-body">
        <div className="entity-view__trip-name">{trip.name}</div>
        <div className="entity-view__trip-meta">
          {dateRange && <div>{dateRange}</div>}
          <div>{eventCountLabel}</div>
        </div>

        {showPeople &&
          <div className="entity-view__trip-footer">
            <div className="entity-view__avatars" aria-label={`People on ${trip.name}`}>
              <MyAvatar
                userAccount={viewingUserAccount}
                style={{height:24, width:24, fontSize:11}}
              />
              {visibleFriendIds.map(id => (
                <MyAvatar
                  key={id}
                  userAccount={friends[id]}
                  style={{height:24, width:24, fontSize:11}}
                />
              ))}
              {hiddenFriendCount > 0 &&
                <div className="entity-view__avatar-count">+{hiddenFriendCount}</div>
              }
            </div>
          </div>
        }
      </div>
    </button>
  );
};

export function TripsView() {
  const user = useSelector(state => state.loggedInUser);
  const trips = useSelector(state => state.viewingUserTrips) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const friends = useSelector(state => state.viewingUserFriends) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const tripSummaries = useMemo(() => buildTripSummaries(events), [events]);
  const sortedTrips = useMemo(
    () => sortTripsByMostRecent(trips, tripSummaries),
    [trips, tripSummaries]
  );

  // Roll up some "trips at a glance" stats for the strip under the hero.
  const totalEventCount = sortedTrips.reduce(
    (sum, trip) => sum + (tripSummaries[trip.id]?.eventCount || 0),
    0
  );
  const longestTrip = useMemo(() => {
    let best = null;
    sortedTrips.forEach(trip => {
      const s = tripSummaries[trip.id];
      if(!s || !s.startAt || !s.endAt) return;
      const ms = s.endAt - s.startAt;
      if(!best || ms > best.ms){
        best = {trip, ms, days: Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1)};
      }
    });
    return best;
  }, [sortedTrips, tripSummaries]);

  const ownerName = viewingUserAccount?.displayName || viewingUserAccount?.name || 'User';
  const heroTitle = isOwnMap ? 'My Trips' : `${ownerName}'s Trips`;
  const heroSubtitle = sortedTrips.length > 0
    ? `${sortedTrips.length} ${sortedTrips.length === 1 ? 'journey' : 'journeys'} to look back on`
    : 'Group events into the trips that hold them';

  const username = viewingUserAccount?.username;
  const openTrip = (trip) => {
    if(!username) return;
    navigateTo(buildDetailPath(username, 'Trips', trip.id));
  };

  const openAddTripDialog = () => {
    dispatch(setDialogOpen('Edit Trip', {
      parentDialog: '',
      dialogTitle: 'Add Trip',
      trip: {
        userId: user?.uid,
        name: '',
      },
    }));
  };

  return (
    <main className="entity-view">
      <section className="entity-view__hero">
        <div className="entity-view__hero-glow entity-view__hero-glow--trips" aria-hidden="true"/>
        <div className="entity-view__hero-content">
          <div className="entity-view__hero-icon" aria-hidden="true">🧳</div>
          <h1 className="entity-view__hero-title">{heroTitle}</h1>
          <div className="entity-view__hero-tagline">Trips</div>
          <div className="entity-view__hero-subtitle">{heroSubtitle}</div>
          {isOwnMap &&
            <div className="entity-view__hero-actions">
              <button
                type="button"
                className="entity-view__hero-add-btn"
                onClick={openAddTripDialog}
              >
                <AddIcon/>
                <span>Add Trip</span>
              </button>
            </div>
          }
        </div>
      </section>

      {sortedTrips.length > 0 &&
        <section className="entity-view__stats-strip" aria-label="Trip stats">
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '120ms', '--stat-accent': '#0ea5e9'}}
          >
            <div className="entity-view__stat-value">{sortedTrips.length}</div>
            <div className="entity-view__stat-label">Trips</div>
          </div>
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '200ms', '--stat-accent': '#6366f1'}}
          >
            <div className="entity-view__stat-value">{totalEventCount}</div>
            <div className="entity-view__stat-label">Events on Trips</div>
          </div>
          {longestTrip &&
            <div
              className="entity-view__stat"
              style={{'--stat-delay': '280ms', '--stat-accent': '#10b981'}}
            >
              <div className="entity-view__stat-value" style={{fontSize:18, lineHeight:1.2}}>
                {longestTrip.trip.name}
              </div>
              <div className="entity-view__stat-label">
                Longest · {longestTrip.days} {longestTrip.days === 1 ? 'day' : 'days'}
              </div>
            </div>
          }
        </section>
      }

      {sortedTrips.length > 0 &&
        <section className="entity-view__panel">
          <header className="entity-view__panel-header">
            <h2>All Trips</h2>
            <span className="entity-view__panel-header-count">
              {sortedTrips.length} total
            </span>
          </header>
          <div className="entity-view__grid entity-view__grid--trips">
            {sortedTrips.map((trip, idx) => (
              <TripCard
                key={trip.id}
                trip={trip}
                summary={tripSummaries[trip.id]}
                friends={friends}
                viewingUserAccount={viewingUserAccount}
                onOpen={() => openTrip(trip)}
                delay={120 + idx * 60}
              />
            ))}
          </div>
        </section>
      }

      {sortedTrips.length === 0 &&
        <section className="entity-view__empty">
          <div className="entity-view__empty-icon" aria-hidden="true">🧳</div>
          <div className="entity-view__empty-title">No trips yet</div>
          <div className="entity-view__empty-text">
            Group events together as a trip to see them as one journey on your map and timeline.
          </div>
          {isOwnMap &&
            <button
              type="button"
              className="entity-view__hero-add-btn"
              onClick={openAddTripDialog}
            >
              <AddIcon/>
              <span>Add your first trip</span>
            </button>
          }
        </section>
      }
    </main>
  );
}
